import { useState } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import ResponseMessage from '@/components/Error/ResponseMessage';
import { VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { TextVariant } from '@/components/Typography/Text.variant';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import AppUserAvatar from '@/features/app-user/components/AppUserAvatar';
import type { ChangePersonProps } from '@/features/app-user/components/ChangePersonDialog/types';
import PersonAvatar from '@/features/app-user/components/PersonAvatar';
import PersonSelect from '@/features/app-user/components/PersonSelect';
import useAppUserChangeWorker from '@/features/app-user/hooks/useAppUserChangeWorker';
import { usePersonActionLabels } from '@/features/app-user/hooks/usePersonActionLabels';
import type { Person } from '@/features/app-user/types';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';

export default function ChangePersonDialog({
  appUser,
  person,
  open,
  onOpenChange,
  softwareId,
}: ChangePersonProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'domain.appUser.component.ChangePersonDialog',
  });
  const tc = useCommonTranslation();
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { dialogTitle, fromLabel, toLabel } = usePersonActionLabels(person);

  const { mutate: changeWorker, isPending } = useAppUserChangeWorker({
    appUserId: appUser.id,
    softwareId,
    onSuccess: () => {
      setSelectedPerson(null);
      onOpenChange?.(false);
    },
  });

  const handleSave = () => {
    if (!selectedPerson) return;
    try {
      changeWorker(selectedPerson.id);
    } catch (_error) {
      setErrorMessage('Error occurred while saving changes. Please try again.');
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedPerson(null);
    }
    onOpenChange?.(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-dialog-lg">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <VStack className="gap-6">
          <VStack className="gap-3">
            <Text
              variant={TextVariant.labelSmall}
              className="text-muted-foreground"
            >
              {fromLabel}
            </Text>
            {person?.id ? (
              <PersonAvatar person={person} />
            ) : (
              <AppUserAvatar appUser={appUser} />
            )}
          </VStack>
          <VStack className="gap-3">
            <Text
              variant={TextVariant.labelSmall}
              className="text-muted-foreground"
            >
              {toLabel}
            </Text>
            <PersonSelect
              value={selectedPerson}
              onValueChange={setSelectedPerson}
              t={t}
            />
          </VStack>
        </VStack>
        <DialogFooter>
          {errorMessage && <ResponseMessage>{errorMessage}</ResponseMessage>}
          <DialogClose asChild>
            <Button variant="ghost">{tc('action.cancel')}</Button>
          </DialogClose>
          <ActionButton onClick={handleSave} isLoading={isPending}>
            {tc('action.save')}
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
